import React from 'react';
import Card from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import { apiFetch } from '../../api.js';
import { useApi } from '../../hooks/useApi.js';

export default function StaffSettings() {
  const { data, loading, error } = useApi('/api/site-settings');
  const [form, setForm] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [sendingTest, setSendingTest] = React.useState(false);
  const [testEmailTo, setTestEmailTo] = React.useState('');
  const [message, setMessage] = React.useState({ text: '', tone: 'info' });

  React.useEffect(() => {
    if (!data) return;
    setForm({
      loginHours: data.login_lifetime ? Math.round(data.login_lifetime / 3600) : '',
      rememberDays: data.remember_lifetime ? Math.round(data.remember_lifetime / 86400) : '',
      advisorEmail: data.advisor_email || '',
      chairEmail: data.chair_email || '',
      maintenance: data.maintenance === 1,
      adminMode: data.admin_mode === 1,
      disableStudents: data.disable_students === 1
    });
  }, [data]);

  const updateField = (key) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    if (!data) return;
    setForm({
      loginHours: data.login_lifetime ? Math.round(data.login_lifetime / 3600) : '',
      rememberDays: data.remember_lifetime ? Math.round(data.remember_lifetime / 86400) : '',
      advisorEmail: data.advisor_email || '',
      chairEmail: data.chair_email || '',
      maintenance: data.maintenance === 1,
      adminMode: data.admin_mode === 1,
      disableStudents: data.disable_students === 1
    });
    setMessage({ text: '', tone: 'info' });
  };

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    setMessage({ text: '', tone: 'info' });
    const payload = {
      login_lifetime: Math.max(0, Number(form.loginHours || 0)) * 3600,
      remember_lifetime: Math.max(0, Number(form.rememberDays || 0)) * 86400,
      advisor_email: form.advisorEmail,
      chair_email: form.chairEmail,
      maintenance: form.maintenance ? 1 : 0,
      admin_mode: form.adminMode ? 1 : 0,
      disable_students: form.disableStudents ? 1 : 0
    };
    try {
      await apiFetch('/api/site-settings', {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      setMessage({ text: 'Settings saved.', tone: 'info' });
    } catch (err) {
      setMessage({ text: err?.message || 'Failed to save settings.', tone: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestEmail = async () => {
    const recipient = String(testEmailTo || '').trim();
    if (!recipient) {
      setMessage({ text: 'Enter a recipient email for the SMTP test.', tone: 'error' });
      return;
    }
    setSendingTest(true);
    setMessage({ text: '', tone: 'info' });
    try {
      const resp = await apiFetch('/api/site-settings/test-email', {
        method: 'POST',
        body: JSON.stringify({ to: recipient })
      });
      setMessage({ text: `Test email sent to ${resp?.to || recipient}.`, tone: 'info' });
    } catch (err) {
      setMessage({ text: err?.message || 'Failed to send test email.', tone: 'error' });
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Site Settings</h1>
          <p className="subtle">Control access policies and notifications.</p>
        </div>
      </div>

      {error ? <div className="alert error">{error}</div> : null}
      {message.text ? <div className={message.tone === 'error' ? 'alert error' : 'alert'}>{message.text}</div> : null}
      {loading || !form ? (
        <p className="subtle">Loading...</p>
      ) : (
        <>
          <div className="grid two">
            <Card title="Access Control">
              <div className="form-grid">
                <Input
                  label="Login Lifetime (hours)"
                  value={form.loginHours}
                  onChange={updateField('loginHours')}
                  type="number"
                  min="0"
                />
                <Input
                  label="Remember Me (days)"
                  value={form.rememberDays}
                  onChange={updateField('rememberDays')}
                  type="number"
                  min="0"
                />
                <Input
                  label="Advisor Email"
                  value={form.advisorEmail}
                  onChange={updateField('advisorEmail')}
                  type="email"
                />
                <Input
                  label="Chair Email"
                  value={form.chairEmail}
                  onChange={updateField('chairEmail')}
                  type="email"
                />
                <Input
                  label="Test Recipient Email"
                  helper="Send a one-time SMTP test email using current .env credentials."
                  value={testEmailTo}
                  onChange={(event) => setTestEmailTo(event.target.value)}
                  type="email"
                />
              </div>
              <div className="form-actions">
                <Button variant="secondary" onClick={handleSendTestEmail} disabled={sendingTest} type="button">
                  {sendingTest ? 'Sending…' : 'Send Test Email'}
                </Button>
              </div>
            </Card>
            <Card title="System Modes">
              <div className="toggle-row">
                <span>Maintenance Mode</span>
                <label className="switch">
                  <input type="checkbox" checked={form.maintenance} onChange={updateField('maintenance')} />
                  <span className="slider" />
                </label>
              </div>
              <div className="toggle-row">
                <span>Admin Only Mode</span>
                <label className="switch">
                  <input type="checkbox" checked={form.adminMode} onChange={updateField('adminMode')} />
                  <span className="slider" />
                </label>
              </div>
              <div className="toggle-row">
                <span>Disable Student Access</span>
                <label className="switch">
                  <input type="checkbox" checked={form.disableStudents} onChange={updateField('disableStudents')} />
                  <span className="slider" />
                </label>
              </div>
            </Card>
          </div>
          <div className="form-actions">
            <Button variant="secondary" onClick={handleReset} type="button">Reset</Button>
            <Button onClick={handleSave} disabled={saving} type="button">{saving ? 'Saving…' : 'Save Changes'}</Button>
          </div>
        </>
      )}
    </div>
  );
}
